import { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Eye, EyeOff, LogIn, ArrowLeft, ArrowRight } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import {
  COPPER, COPPER_LIGHT, CREAM, DARK_BG, DARK_BG_2,
  DISPLAY_FF, ITALIC_FF, GRAIN_BG, GLASS_STYLE, COPPER_BTN_STYLE,
  CopperHalo, Eyebrow, Shimmer,
} from '../../components/vitrine/theme';

// Reuse the AI-generated luxury marble hero image (Taj Mahal quartzite atelier)
const heroImage = "/hero-granipal.jpg";
const LOGO_URL = "/logo.png";

const inputStyle = {
  background: 'rgba(255,255,255,0.04)',
  border: '1px solid rgba(212,168,120,0.30)',
  borderRadius: '2px',
  color: CREAM,
  fontFamily: 'Outfit, sans-serif',
};

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || '/dashboard';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await login(email, password);
      toast.success(`Bienvenue, ${user.contact_name} !`);
      if (user.role === 'admin') navigate('/admin');
      else navigate(from);
    } catch (error) {
      const message = error.response?.data?.detail || 'Erreur de connexion';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" style={{ background: DARK_BG, fontFamily: 'Outfit, sans-serif' }}>
      {/* ============== LEFT — Taj Mahal quartzite atelier image ============== */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <img
          src={heroImage}
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
          style={{ filter: 'brightness(0.92) saturate(1.05)' }}
        />
        {/* Dark fade to the right so form side blends naturally */}
        <div aria-hidden className="absolute inset-0" style={{ background: 'linear-gradient(90deg, rgba(18,11,7,0.20) 0%, rgba(18,11,7,0.05) 35%, rgba(18,11,7,0.45) 80%, rgba(18,11,7,0.85) 100%)' }} />
        {/* Bottom dark fade for text readability */}
        <div aria-hidden className="absolute inset-x-0 bottom-0 h-2/3" style={{ background: 'linear-gradient(180deg, transparent 0%, rgba(18,11,7,0.65) 60%, rgba(18,11,7,0.92) 100%)' }} />
        <div aria-hidden className="absolute inset-0 pointer-events-none opacity-[0.10] mix-blend-overlay" style={{ backgroundImage: GRAIN_BG, backgroundSize: '220px 220px' }} />

        {/* Content */}
        <div className="relative z-10 p-14 xl:p-20 flex flex-col justify-between w-full">
          <Link to="/" className="inline-flex">
            <img src={LOGO_URL} alt="Granipal" className="h-20 xl:h-24 w-auto" />
          </Link>

          <div className="max-w-lg">
            <Eyebrow>Accès partenaires</Eyebrow>
            <h1
              className="mb-6"
              style={{
                fontFamily: DISPLAY_FF,
                fontSize: 'clamp(2.2rem, 3.6vw, 3.4rem)',
                fontWeight: 400,
                color: CREAM,
                letterSpacing: '0.025em',
                lineHeight: 1.08,
                textShadow: '0 2px 12px rgba(0,0,0,0.4)',
              }}
            >
              Plateforme professionnelle{' '}
              <em style={{ fontFamily: ITALIC_FF, fontStyle: 'italic', color: COPPER_LIGHT, fontWeight: 400 }}>
                Granipal
              </em>
            </h1>
            <p className="font-light leading-relaxed text-[15px] max-w-md" style={{ color: 'rgba(245,230,208,0.78)', textShadow: '0 1px 8px rgba(0,0,0,0.35)' }}>
              Configurez vos devis, gérez vos commandes et accédez à nos tarifs professionnels —
              un outil pensé pour les artisans, architectes et bureaux d'études partenaires.
            </p>
            <div className="mt-10 flex items-center gap-3">
              <span aria-hidden className="block w-10 h-px" style={{ background: COPPER_LIGHT, opacity: 0.7 }} />
              <span className="text-[10px] uppercase tracking-[0.4em]" style={{ color: COPPER_LIGHT }}>Réservé aux comptes professionnels</span>
            </div>
          </div>
        </div>
      </div>

      {/* ============== RIGHT — form ============== */}
      <div className="relative w-full lg:w-1/2 flex items-center justify-center p-8 lg:p-14 overflow-hidden" style={{ background: DARK_BG }}>
        <CopperHalo x="50%" y="40%" size={1000} opacity={0.10} />
        <CopperHalo x="20%" y="15%" size={600} opacity={0.08} />
        <CopperHalo x="80%" y="85%" size={600} opacity={0.08} />
        <div aria-hidden className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(ellipse at 15% 15%, rgba(184,137,90,0.08) 0%, transparent 50%), radial-gradient(ellipse at 85% 85%, rgba(212,168,120,0.06) 0%, transparent 50%)' }} />
        <div aria-hidden className="absolute inset-0 pointer-events-none opacity-[0.10] mix-blend-overlay" style={{ backgroundImage: GRAIN_BG, backgroundSize: '220px 220px' }} />

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }} className="relative w-full max-w-md">
          {/* Mobile logo */}
          <div className="lg:hidden mb-8 text-center">
            <Link to="/"><img src={LOGO_URL} alt="Granipal" className="h-16 w-auto mx-auto" /></Link>
          </div>

          <Link
            to="/"
            className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.3em] mb-10 transition-colors"
            style={{ color: 'rgba(245,230,208,0.55)' }}
            onMouseEnter={(e) => { e.currentTarget.style.color = COPPER_LIGHT; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = 'rgba(245,230,208,0.55)'; }}
            data-testid="back-to-site"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            Retour au site
          </Link>

          <div className="relative px-8 py-10 lg:px-10 lg:py-12" style={{ ...GLASS_STYLE, borderRadius: '2px' }}>
            <Eyebrow>Connexion</Eyebrow>
            <h2 className="mb-2" style={{ fontFamily: DISPLAY_FF, fontSize: '1.7rem', fontWeight: 400, color: CREAM, letterSpacing: '0.02em' }}>
              Accédez à <em style={{ fontFamily: ITALIC_FF, fontStyle: 'italic', color: COPPER_LIGHT }}>votre espace</em>
            </h2>
            <p className="mb-8 text-[13px] font-light leading-relaxed" style={{ color: 'rgba(245,230,208,0.6)' }}>
              Identifiants fournis par l'administration Granipal.
            </p>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="text-[10px] uppercase tracking-[0.3em] mb-2 block" style={{ color: 'rgba(245,230,208,0.65)' }}>Email professionnel</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  placeholder="vous@entreprise.fr"
                  className="w-full px-4 py-3 text-sm outline-none placeholder:text-white/30 focus:border-[#B8895A] transition"
                  style={inputStyle}
                  data-testid="login-email"
                />
              </div>

              <div>
                <label className="text-[10px] uppercase tracking-[0.3em] mb-2 block" style={{ color: 'rgba(245,230,208,0.65)' }}>Mot de passe</label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    placeholder="••••••••"
                    className="w-full px-4 py-3 pr-12 text-sm outline-none placeholder:text-white/30 focus:border-[#B8895A] transition"
                    style={inputStyle}
                    data-testid="login-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 transition-colors"
                    style={{ color: 'rgba(245,230,208,0.5)' }}
                    aria-label="Afficher / masquer le mot de passe"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="group relative w-full inline-flex items-center justify-center gap-3 px-10 py-4 uppercase text-[11px] tracking-[0.3em] transition-all duration-500 overflow-hidden disabled:opacity-50"
                style={{ ...COPPER_BTN_STYLE, borderRadius: '2px' }}
                data-testid="login-submit"
              >
                <Shimmer />
                {loading ? (
                  <span className="relative w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <>
                    <LogIn className="relative w-4 h-4" />
                    <span className="relative">Se connecter</span>
                    <ArrowRight className="relative w-3.5 h-3.5 group-hover:translate-x-1 transition-transform duration-500" />
                  </>
                )}
              </button>
            </form>
          </div>

          <p className="text-[11px] text-center mt-10 font-light leading-relaxed" style={{ color: 'rgba(245,230,208,0.45)' }}>
            Compte créé par l'administrateur Granipal uniquement.
            <br />
            <Link to="/contact" className="underline underline-offset-4 transition-colors" style={{ color: COPPER_LIGHT, textDecorationColor: 'rgba(212,168,120,0.5)' }}>
              Contactez-nous
            </Link>{' '}pour obtenir vos accès.
          </p>
        </motion.div>
      </div>
    </div>
  );
}
